import IconButton from 'components/IconButton';
import React, { useEffect, useMemo } from 'react';
import { CardHeader, Row, Col } from 'reactstrap';

import VulnerabilitiesListing from './vulnListing';
import { ChartType, GroupingDataItem, ModalDialogData } from '../../types';
import BarOrDoughnutChart from '../BarOrDoughnutChart';
import { ChartColorMode } from '../BarOrDoughnutChart/helper';
import './styles.css';

interface Props {
  title: string;
  data: GroupingDataItem[];
  severityFilters: string[];
  sourceType: string;
  chartType: ChartType;
  showQidColumn?: boolean;
  isVulnListingOpen?: boolean; //SUGGESTION: update other charts and make this non-optional
  hideIgnore?: boolean;
  onExpandClick: (data: ModalDialogData | undefined) => void;
  getDrillDownUrl: (qid: string, sourceType: string) => string;
}

const OTHERS_LABEL = 'All Others';
const OTHERS_QID = 'Others';

const GroupingChart = ({
  title,
  data,
  severityFilters,
  sourceType,
  chartType,
  showQidColumn = true,
  isVulnListingOpen = false,
  hideIgnore = false,
  getDrillDownUrl,
  onExpandClick,
}: Props) => {
  const { vulnListingData, chartData } = useMemo(() => {
    if (data && data.length > 0) {
      const sortedData = [...data].sort((a, b) => {
        if (a.qid === OTHERS_QID) return 1;
        if (b.qid === OTHERS_QID) return -1;

        return a.count > b.count ? -1 : 1;
      });

      const labels = sortedData.map((x) => x.name);
      const qids = sortedData.map((x) => x.qid);
      const counts = sortedData.map((x) => x.count);

      //  process the data for pie chart
      // select top ten and others
      const tt_labels = labels.slice(0, 10);
      const tt_qids = qids.slice(0, 10);
      const tt_counts = counts.slice(0, 10);

      let othersCount = 0;

      if (counts.length > 10) {
        for (let index = 10; index < counts.length; index++) {
          othersCount += counts[index];
        }
      }

      if (othersCount > 0) {
        tt_labels.push(OTHERS_LABEL);
        tt_qids.push(OTHERS_QID);
        tt_counts.push(othersCount);
      }

      return {
        vulnListingData: { labels, qids, counts },
        chartData: { labels: tt_labels, qids: tt_qids, counts: tt_counts },
      };
    }

    return {
      vulnListingData: {
        labels: [] as string[],
        qids: [] as string[],
        counts: [] as number[],
      },
      chartData: {
        labels: [] as string[],
        qids: [] as string[],
        counts: [] as number[],
      },
    };
  }, [data]);

  useEffect(() => {
    //  hide the vuln listing dialog
    onExpandClick(undefined);
    if (data && data.length > 0 && isVulnListingOpen) {
      // only show if it was open before the data reload.
      openVulnListingDialog(
        vulnListingData.qids,
        vulnListingData.labels,
        vulnListingData.counts,
      );
    }
  }, [data]);
  // NOTE: keep the sourceTypeParam param, but sourceTypeParam SHOULD NOT BE USED, instead use the src type passed in component props
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getGroupingDrillDownUrl(label: string, sourceTypeParam: string) {
    const labelIndex = chartData.labels.indexOf(label);
    const qid = chartData.qids[labelIndex];
    const labels = chartData.labels[labelIndex];
    if (qid && qid !== OTHERS_QID) {
      const url = getDrillDownUrl(qid, sourceType);
      return url;
    } else if (qid === OTHERS_QID) {
      return '';
    }
    const url = getDrillDownUrl(labels, sourceType);
    return url;
  }

  const getSubTitle = (qidsCount): JSX.Element => (
    <h3>{qidsCount.toString()}</h3>
  );

  const getVulnListing = (qids, labels, counts) => {
    return (
      <VulnerabilitiesListing
        qids={qids}
        names={labels}
        counts={counts}
        sourceType={sourceType}
        getGroupingDrillDownUrl={getDrillDownUrl}
        showQidColumn={showQidColumn}
        hideIgnore={hideIgnore}
      />
    );
  };

  const openVulnListingDialog = (qids, labels, counts) => {
    if (qids && qids.length > 0) {
      onExpandClick({
        title: title,
        subTitle: getSubTitle(qids.length),
        bodyComponent: getVulnListing(qids, labels, counts),
      });
    }
  };

  const chartHeader = (
    <CardHeader>
      <Row>
        <Col className="d-flex flex-row align-items-baseline">
          <h2 className="d-inline mb-0">Top 10</h2>
          <h5 className="card-category px-1">{title}</h5>
        </Col>
        <Col>
          <div className="d-inline float-right">
            <IconButton
              id="view_details"
              iconClass="bi bi-table"
              size="sm"
              cssClass="padding-2"
              tooltip="View Details"
              onClick={() =>
                openVulnListingDialog(
                  vulnListingData.qids,
                  vulnListingData.labels,
                  vulnListingData.counts,
                )
              }
            />
          </div>
        </Col>
      </Row>
    </CardHeader>
  );

  return (
    <BarOrDoughnutChart
      chartType={chartType}
      title={title}
      labels={chartData.labels || []}
      tooltipLabels={[]}
      datasets={[chartData.counts || []]}
      severityFilters={severityFilters}
      customChartHeader={chartHeader}
      colorMode={ChartColorMode.Scale}
      getDrillDownUrl={getGroupingDrillDownUrl}
    />
  );
};

export default GroupingChart;
